import type { CycleGroup, OrgEmployee } from "./types";

import { getDisplayFields } from "./data";
import type { LayoutNode } from "./layout";

interface ChartRenderData {
	visibleEmployees: OrgEmployee[];
	positions: Map<string, LayoutNode>;
	edges: Array<{ source: string; target: string }>;
	cycleGroups: CycleGroup[];
	offsetX: number;
	offsetY: number;
	contentWidth: number;
	contentHeight: number;
}

const CARD_WIDTH = 280;
const CARD_HEIGHT = 120;
const TOGGLE_BUTTON_RADIUS = 16;
const BRANCH_STEM_LENGTH = 42;
const TARGET_STEM_MIN_LENGTH = 18;
const MAX_CANVAS_DIMENSION = 16384;
const MAX_CANVAS_AREA = 268435456;
const FONT_STACK = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const AVATAR_PALETTE: Array<{ bg: string; fg: string }> = [
	{ bg: "#d8edf6", fg: "#24516b" },
	{ bg: "#dff6ea", fg: "#11613c" },
	{ bg: "#fff0ca", fg: "#8a5600" },
	{ bg: "#ffe2e6", fg: "#98213f" },
	{ bg: "#e8e2ff", fg: "#5b3ab5" },
	{ bg: "#d5f5f7", fg: "#14606a" },
	{ bg: "#e0f0ff", fg: "#1f5a8a" },
	{ bg: "#fde3f0", fg: "#9a2f66" },
	{ bg: "#ecf7d4", fg: "#4f6b13" },
	{ bg: "#ffe3cc", fg: "#8d4315" },
];

function departmentPalette(department: string): { bg: string; fg: string } {
	let hash = 0;
	for (let i = 0; i < department.length; i += 1) {
		hash = department.charCodeAt(i) + (hash << 5) - hash;
	}
	return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}

function downloadBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 0);
}

function escapeXml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");
}

function truncate(value: string, maxLength: number): string {
	if (value.length <= maxLength) return value;
	return `${value.slice(0, Math.max(0, maxLength - 1))}...`;
}

function buildSvgString(data: ChartRenderData): string {
	const { visibleEmployees, positions, edges, cycleGroups, offsetX, offsetY, contentWidth, contentHeight } = data;
	const cycleMembers = new Set<string>();
	for (const group of cycleGroups) {
		for (const id of group.memberIds) {
			cycleMembers.add(id);
		}
	}

	const parts: string[] = [];
	parts.push(
		`<svg xmlns="http://www.w3.org/2000/svg" width="${contentWidth}" height="${contentHeight}" viewBox="0 0 ${contentWidth} ${contentHeight}">`
	);
	parts.push(`<rect width="100%" height="100%" fill="#ffffff"/>`);

	for (const group of cycleGroups) {
		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;
		for (const id of group.memberIds) {
			const position = positions.get(id);
			if (!position) continue;
			minX = Math.min(minX, position.x + offsetX);
			minY = Math.min(minY, position.y + offsetY);
			maxX = Math.max(maxX, position.x + position.width + offsetX);
			maxY = Math.max(maxY, position.y + position.height + offsetY);
		}
		if (!isFinite(minX)) continue;
		const padding = 20;
		const x = minX - padding;
		const y = minY - padding - 24;
		const width = maxX - minX + padding * 2;
		const height = maxY - minY + padding * 2 + 24;
		parts.push(
			`<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="8" fill="#2f7ea8" fill-opacity="0.05" stroke="#2f7ea8" stroke-opacity="0.45" stroke-dasharray="6 4" stroke-width="1"/>`
		);
		parts.push(
			`<text x="${x + 10}" y="${y + 16}" font-family="${FONT_STACK}" font-size="12" font-weight="600" fill="#2f6f95">Peer group (${group.memberIds.length})</text>`
		);
	}

	for (const edge of edges) {
		const source = positions.get(edge.source);
		const target = positions.get(edge.target);
		if (!source || !target) continue;
		const sx = source.x + source.width / 2 + offsetX;
		const sy = source.y + source.height + offsetY + TOGGLE_BUTTON_RADIUS;
		const tx = target.x + target.width / 2 + offsetX;
		const ty = target.y + offsetY;
		const midY = Math.min(sy + BRANCH_STEM_LENGTH, Math.max(sy, ty - TARGET_STEM_MIN_LENGTH));
		const path = `M ${sx} ${sy} L ${sx} ${midY} L ${tx} ${midY} L ${tx} ${ty}`;
		parts.push(
			`<path d="${path}" fill="none" stroke="#6b7280" stroke-opacity="0.5" stroke-width="1.5" stroke-dasharray="6 3"/>`
		);
	}

	for (const employee of visibleEmployees) {
		const position = positions.get(employee.id);
		if (!position) continue;
		const x = position.x + offsetX;
		const y = position.y + offsetY;
		const palette = departmentPalette(employee.department || employee.name);
		const stroke = cycleMembers.has(employee.id) ? "#9cc6dc" : "#d7dde5";
		const displayFields = getDisplayFields(employee);
		const primaryFieldValue = displayFields[0]?.value ?? "";
		const secondaryFieldValue = displayFields[1]?.value ?? "";

		parts.push(`<g transform="translate(${x} ${y})">`);
		parts.push(
			`<rect x="0" y="0" width="${CARD_WIDTH}" height="${CARD_HEIGHT}" rx="8" fill="#ffffff" stroke="${stroke}" stroke-width="1"/>`
		);
		parts.push(`<circle cx="36" cy="34" r="20" fill="${palette.bg}"/>`);
		parts.push(
			`<text x="64" y="34" font-family="${FONT_STACK}" font-size="16" font-weight="600" fill="#286d92">${escapeXml(
				truncate(employee.name, 24)
			)}</text>`
		);
		parts.push(
			`<text x="16" y="78" font-family="${FONT_STACK}" font-size="14" font-weight="600" fill="#111827">${escapeXml(
				truncate(primaryFieldValue, 34)
			)}</text>`
		);
		parts.push(
			`<text x="16" y="99" font-family="${FONT_STACK}" font-size="13" font-weight="400" fill="#6b7280">${escapeXml(
				truncate(secondaryFieldValue, 36)
			)}</text>`
		);
		parts.push("</g>");
	}

	parts.push("</svg>");
	return parts.join("");
}

// Exceeding either browser canvas cap yields a blank canvas or a null blob, so scale down to fit.
function fitPixelRatio(width: number, height: number, requested: number): number {
	if (width <= 0 || height <= 0) return requested;
	const byDimension = Math.min(MAX_CANVAS_DIMENSION / width, MAX_CANVAS_DIMENSION / height);
	const byArea = Math.sqrt(MAX_CANVAS_AREA / (width * height));
	return Math.min(requested, byDimension, byArea);
}

async function renderSvgToBlob(svgString: string, width: number, height: number, pixelRatio: number): Promise<Blob> {
	const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	try {
		const image = new Image();
		image.crossOrigin = "anonymous";
		await new Promise<void>((resolve, reject) => {
			image.onload = () => resolve();
			image.onerror = () => reject(new Error("SVG image load failed"));
			image.src = url;
		});

		const canvas = document.createElement("canvas");
		canvas.width = Math.ceil(width * pixelRatio);
		canvas.height = Math.ceil(height * pixelRatio);
		const context = canvas.getContext("2d");
		if (!context) throw new Error("Could not acquire canvas context");
		context.scale(pixelRatio, pixelRatio);
		context.fillStyle = "#ffffff";
		context.fillRect(0, 0, width, height);
		context.drawImage(image, 0, 0, width, height);

		// Encode straight to a blob: fetching a canvas data: URL is blocked by our connect-src CSP.
		return await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob((encoded) => {
				if (encoded) resolve(encoded);
				else reject(new Error("Could not encode the org chart PNG"));
			}, "image/png");
		});
	} finally {
		URL.revokeObjectURL(url);
	}
}

export async function exportToPng(data: ChartRenderData & { filename: string; pixelRatio?: number }): Promise<void> {
	const svg = buildSvgString(data);
	const pixelRatio = fitPixelRatio(data.contentWidth, data.contentHeight, data.pixelRatio ?? 2);
	const blob = await renderSvgToBlob(svg, data.contentWidth, data.contentHeight, pixelRatio);
	downloadBlob(blob, data.filename);
}
